/**
 * The response keys an operation's root selection asks for: each field's alias, or its name when it
 * has none (I-0748).
 *
 * A GraphQL server answers every one of them, with a value or with `null`, unless the answer carries
 * errors. So a key missing from `data` means the answer is malformed, and `requireRootFields` says so
 * before a caller reads a property of `undefined` and a page crashes while drawing.
 *
 * Returns `null` when the root selection cannot be read without a schema: a fragment spread, or a
 * field under `@skip` / `@include`, whose key may rightly be absent.
 */
export function rootResponseKeys(document: string): string[] | null {
  const tokens = tokenize(document);
  let index = 0;

  // Past every fragment definition to the operation's own `{`.
  while (index < tokens.length) {
    const token = tokens[index];
    if (token === '{') break;
    if (token === 'fragment') {
      while (index < tokens.length && tokens[index] !== '{') index += 1;
      index = skipBalanced(tokens, index, '{', '}');
      continue;
    }
    if (token === '(') {
      index = skipBalanced(tokens, index, '(', ')');
      continue;
    }
    index += 1;
  }
  if (tokens[index] !== '{') return null;
  index += 1;

  const keys: string[] = [];
  while (index < tokens.length && tokens[index] !== '}') {
    const token = tokens[index];
    if (token === '...') return null;
    if (!isName(token)) return null;
    const key = token;
    index += 1;
    if (tokens[index] === ':') {
      // `alias: field` — the answer is keyed by the alias.
      index += 2;
    }
    if (tokens[index] === '(') index = skipBalanced(tokens, index, '(', ')');
    while (tokens[index] === '@') {
      const directive = tokens[index + 1];
      if (directive === 'skip' || directive === 'include') return null;
      index += 2;
      if (tokens[index] === '(') index = skipBalanced(tokens, index, '(', ')');
    }
    if (tokens[index] === '{') index = skipBalanced(tokens, index, '{', '}');
    keys.push(key);
  }
  return keys;
}

/**
 * `data`, once every root field `document` asks for is in it. Throws an error naming the operation
 * and the missing fields otherwise, so the caller's load-failure path reports a malformed answer
 * instead of a component crashing on `undefined`.
 */
export function requireRootFields<T>(document: string, data: T): T {
  const keys = rootResponseKeys(document);
  if (!keys || typeof data !== 'object' || data === null) return data;
  const missing = keys.filter((key) => !(key in data));
  if (missing.length === 0) return data;
  const operation = /\b(?:query|mutation|subscription)\s+([_A-Za-z][_0-9A-Za-z]*)/.exec(document);
  throw new Error(
    `The server's answer to ${operation ? operation[1] : 'a request'} is missing ${missing.join(', ')}`,
  );
}

const NAME = /^[_A-Za-z][_0-9A-Za-z]*$/;

function isName(token: string | undefined): token is string {
  return token !== undefined && NAME.test(token);
}

/** The index just past the `close` that matches the `open` at `start`. */
function skipBalanced(tokens: string[], start: number, open: string, close: string): number {
  let depth = 0;
  for (let index = start; index < tokens.length; index += 1) {
    if (tokens[index] === open) depth += 1;
    else if (tokens[index] === close) {
      depth -= 1;
      if (depth === 0) return index + 1;
    }
  }
  return tokens.length;
}

/** Names, punctuators and `...`; strings and comments are dropped, since neither has structure. */
function tokenize(document: string): string[] {
  const tokens: string[] = [];
  const pattern =
    /"""[\s\S]*?"""|"(?:[^"\\\n]|\\.)*"|#[^\n]*|\.\.\.|[_A-Za-z][_0-9A-Za-z]*|-?\d[\d.eE+-]*|[{}()[\]:@$!=|&]/g;
  for (const match of document.matchAll(pattern)) {
    const token = match[0];
    if (token.startsWith('"') || token.startsWith('#')) continue;
    tokens.push(token);
  }
  return tokens;
}
